import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { aiSearchPatternService } from "../services/aiSearchPatternService";
import { aiSearchPatternDisplayId } from "./AiSearchPatternCommonStore";
import { AiSearchPattern } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class AiSearchPatternViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = aiSearchPatternService;
  aiSearchPatternInstance: AiSearchPattern | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  descriptionValue: string = "";
  patternValue: string = "";
  pathTypesValue: string[] = [];
  toolsValue: string[] = [];
  effectivenessValue: number | undefined = undefined;
  evaluationCountValue: number = 0;
  successCountValue: number = 0;
  sourceEvalIdsValue: string[] = [];
  proposedByValue: string = "";
  activeValue: boolean = false;
  priorityValue: number = 0;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAiSearchPatternById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.aiSearchPatternOne({ id });
    this.aiSearchPatternInstance = result.aiSearchPatternOne;
    if (this.aiSearchPatternInstance) {
      this.loadValues(this.aiSearchPatternInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.aiSearchPatternInstance) {
      return aiSearchPatternDisplayId(
        this.aiSearchPatternInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AiSearchPatternSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.aiSearchPatternInstance = null;
    this.nameValue = "";
    this.descriptionValue = "";
    this.patternValue = "";
    this.pathTypesValue = [];
    this.toolsValue = [];
    this.effectivenessValue = undefined;
    this.evaluationCountValue = 0;
    this.successCountValue = 0;
    this.sourceEvalIdsValue = [];
    this.proposedByValue = "";
    this.activeValue = false;
    this.priorityValue = 0;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAiSearchPattern() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("aiSearchPattern_update");
  }

  editAiSearchPattern() {
    const id = this.aiSearchPatternInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/aiSearchPattern/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAiSearchPattern(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAiSearchPattern(): any {
    yield this.close();
  }

  loadValues(instance: AiSearchPattern) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.descriptionValue = instance.description;
    this.patternValue = instance.pattern;
    this.pathTypesValue = instance.pathTypes || [];
    this.toolsValue = instance.tools || [];
    this.effectivenessValue = instance.effectiveness || undefined;
    this.evaluationCountValue = instance.evaluationCount;
    this.successCountValue = instance.successCount;
    this.sourceEvalIdsValue = instance.sourceEvalIds || [];
    this.proposedByValue = instance.proposedBy || "";
    this.activeValue = instance.active;
    this.priorityValue = instance.priority;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAiSearchPatternById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
